import {useRef, useMemo} from 'react';
import {useQueryClient, QueryClient} from '@tanstack/react-query';

import {ICategory} from 'coreStore/data/categories/types';
import {useCommerceAPI} from 'commerceAPI/contexts';
import CommerceAPI from 'commerceAPI';
import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import {useRequestClass} from 'coreHooks/index';
import useLanguageCode from 'coreHooks/locales/useLanguageCode';

import {
	fetchCategoryTree,
	getCategoriesQueryKey,
	createCategoryResult,
	ICategoryResult,
	CategoryDeps,
} from 'coreUtils/categories';

interface CategoryActionsDeps {
	queryClient: QueryClient;
	commerceApi: CommerceAPI;
	lang: string;
	categoryDeps: CategoryDeps;
}

export interface ICategoryActions {
	fetchAllCategories: () => Promise<Record<string, ICategory>>;
	prefetchCategory: (cgid: string) => Promise<ICategory | null>;
	getCategory: (cgid: string, fetchIfNotExists?: boolean) => ICategoryResult;
	getCategoryPromise: (cgid: string) => Promise<ICategoryResult>;
	getStoredCategory: (cgid: string) => ICategory | null;
	getSubCategories: (category: ICategory | undefined) => ICategory[];
	getSizeGuideIds: (category: ICategory | undefined) => string;
	getLastUpdate: () => number;
}

/**
 * Imperative category actions with a stable return reference.
 * Use in _app-config, event handlers, and SSR paths.
 * UI components should prefer useCategory(id) for granular reactivity.
 */
export default function useCategoryActions(): ICategoryActions {
	const queryClient = useQueryClient();
	const commerceApi = useCommerceAPI();
	const lang = useLanguageCode();
	const {managerRepository} = useGeneralProvider();
	const {isCORSRequest} = useRequestClass();
	const localesMgr = managerRepository.getLocalesMgr();

	const depsRef = useRef<CategoryActionsDeps>({
		queryClient,
		commerceApi,
		lang,
		categoryDeps: {localesMgr, isCORSRequest, queryClient, languageCode: lang},
	});
	depsRef.current = {
		queryClient,
		commerceApi,
		lang,
		categoryDeps: {localesMgr, isCORSRequest, queryClient, languageCode: lang},
	};

	return useMemo((): ICategoryActions => ({
		fetchAllCategories: () => {
			const {commerceApi, queryClient, lang} = depsRef.current;

			return fetchCategoryTree('root', commerceApi, queryClient, lang);
		},

		prefetchCategory: (cgid: string) => {
			const {commerceApi, queryClient, lang} = depsRef.current;

			return fetchCategoryTree(cgid, commerceApi, queryClient, lang);
		},

		getCategory: (cgid: string, fetchIfNotExists = false): ICategoryResult => {
			const {queryClient, lang, categoryDeps} = depsRef.current;
			const cache = queryClient.getQueryData<Record<string, ICategory>>(
				getCategoriesQueryKey(lang),
			) || {};

			if (fetchIfNotExists && cache['root'] && cgid && (!cache[cgid] || cache[cgid].toBeRefreshed)) {
				const {commerceApi} = depsRef.current;

				fetchCategoryTree(cgid, commerceApi, queryClient, lang);
			}

			return createCategoryResult(cache[cgid] ?? null, categoryDeps);
		},

		getCategoryPromise: async (cgid: string): Promise<ICategoryResult> => {
			const {queryClient, lang, categoryDeps, commerceApi} = depsRef.current;
			const cache = queryClient.getQueryData<Record<string, ICategory>>(
				getCategoriesQueryKey(lang),
			) || {};

			if (cache['root'] && cgid && (!cache[cgid] || cache[cgid].toBeRefreshed)) {
				await fetchCategoryTree(cgid, commerceApi, queryClient, lang);
			}

			const updatedCache = queryClient.getQueryData<Record<string, ICategory>>(
				getCategoriesQueryKey(lang),
			) || {};

			return createCategoryResult(updatedCache[cgid] ?? null, categoryDeps);
		},

		getStoredCategory: (cgid: string): ICategory | null => {
			const {queryClient, lang} = depsRef.current;
			const cache = queryClient.getQueryData<Record<string, ICategory>>(
				getCategoriesQueryKey(lang),
			) || {};

			return cache[cgid] || null;
		},

		getSubCategories: (category: ICategory | undefined): ICategory[] => {
			if (!category?.categories) {
				return [];
			}

			const {queryClient, lang} = depsRef.current;
			const cache = queryClient.getQueryData<Record<string, ICategory>>(
				getCategoriesQueryKey(lang),
			) || {};

			return category.categories.reduce<ICategory[]>((arr, cgid) => {
				const stored = cache[cgid];

				if (stored && !stored._notFound) {
					arr.push(stored);
				}

				return arr;
			}, []);
		},

		getSizeGuideIds: (category: ICategory | undefined): string => {
			if (!category) {
				return '';
			}

			const {queryClient, lang} = depsRef.current;
			const cache = queryClient.getQueryData<Record<string, ICategory>>(
				getCategoriesQueryKey(lang),
			) || {};

			let current: ICategory | undefined = category;
			let sizeGuideId = category.c_sizeGuide;

			while (!sizeGuideId && current?.parentCategoryId) {
				current = cache[current.parentCategoryId];

				if (current?._notFound) {
					break;
				}

				sizeGuideId = current?.c_sizeGuide;
			}

			return sizeGuideId || '';
		},

		getLastUpdate: (): number => {
			const {queryClient, lang} = depsRef.current;
			const state = queryClient.getQueryState(getCategoriesQueryKey(lang));

			return state?.dataUpdatedAt || 0;
		},
	}), []);
}
